import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  type Location,
} from 'react-router-dom'
import { AuthProvider } from '@/hooks/use-auth'
import { StreamChatProvider } from '@/hooks/use-stream-chat'
import { FoundationShell } from '@/components/layout/FoundationShell'
import { SignInModal } from '@/components/auth/SignInModal'
import { FoundationHomePage } from '@/pages/FoundationHomePage'
import { PropertyProfileRedirect } from '@/pages/PropertyProfileRedirect'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { VerifyEmailPage } from '@/pages/VerifyEmailPage'
import { ResetPasswordPage } from '@/pages/ResetPasswordPage'
import { BiddlesPage } from '@/pages/BiddlesPage'
import { CollectionsPage } from '@/pages/CollectionsPage'
import { CollectionDetailPage } from '@/pages/CollectionDetailPage'
import { PropertiesPage } from '@/pages/PropertiesPage'
import { MyPropertyBiddlesPage } from '@/pages/MyPropertyBiddlesPage'
import { CreateBiddleRedirect } from '@/pages/CreateBiddleRedirect'
import { BiddleDetailRedirect } from '@/pages/BiddleDetailRedirect'
import { ClaimPropertyPage } from '@/pages/ClaimPropertyPage'
import { GotALetterPage } from '@/pages/GotALetterPage'
import { VerifyIdentityModal } from '@/components/auth/VerifyIdentityModal'
import { SettingsModal } from '@/components/settings/SettingsModal'
import { LettersPage } from '@/pages/LettersPage'
import { ChatPage } from '@/pages/ChatPage'
import { NameRevealPage } from '@/pages/NameRevealPage'
import { McpOAuthPage } from '@/pages/McpOAuthPage'
import '@/lib/firebase'

function AppRoutes() {
  const location = useLocation()
  const state = location.state as { background?: Location } | null
  const isSignIn = location.pathname === '/signin' || location.pathname === '/sign-in'
  const isVerifyIdentity = location.pathname === '/verify-identity'
  const isSettings = location.pathname === '/settings'
  const isOverlay = isSignIn || isVerifyIdentity || isSettings

  // Keep the prior page (or `next` deep-link target) visible underneath the modal.
  const nextParam = new URLSearchParams(location.search).get('next')
  const nextPath =
    nextParam && nextParam.startsWith('/') ? nextParam.split('?')[0] || '/search' : '/search'
  const backgroundLocation =
    state?.background ??
    (isOverlay
      ? ({
          ...location,
          pathname: nextPath,
          search: '',
          hash: '',
          state: null,
          key: isSignIn ? 'signin-bg' : isSettings ? 'settings-bg' : 'verify-bg',
        } satisfies Location)
      : location)

  return (
    <>
      <Routes location={isOverlay ? backgroundLocation : location}>
        <Route path="/sign-in" element={<Navigate to="/signin" replace />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Campaign letter landing — full-bleed marketing page, outside app shell. */}
        <Route path="/o" element={<GotALetterPage />} />
        <Route path="/o/:code" element={<GotALetterPage />} />

        {/* Claude MCP connector OAuth bridge (Firebase sign-in → MCP auth code). */}
        <Route path="/mcp-oauth" element={<McpOAuthPage />} />

        <Route element={<FoundationShell />}>
          <Route path="/" element={<Navigate to="/search" replace />} />
          <Route path="/search" element={<FoundationHomePage />} />
          {/* Map is keep-alive mounted in FoundationShell so camera/pins persist across tabs. */}
          <Route path="/map" element={null} />
          <Route path="/property/:propertyId" element={<PropertyProfileRedirect />} />
          <Route path="/property/:propertyId/biddle/new" element={<CreateBiddleRedirect />} />
          <Route path="/biddle/:propertyId/:biddleId" element={<BiddleDetailRedirect />} />
          <Route path="/biddles" element={<BiddlesPage />} />
          <Route path="/collections" element={<CollectionsPage />} />
          <Route path="/collections/:collectionId" element={<CollectionDetailPage />} />
          <Route path="/properties" element={<PropertiesPage />} />
          <Route path="/properties/:propertyId/biddles" element={<MyPropertyBiddlesPage />} />
          <Route path="/claim-property" element={<ClaimPropertyPage />} />
          <Route path="/letters" element={<LettersPage />} />
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/chat/:channelId" element={<ChatPage />} />
          <Route path="/name-reveal/:chatId" element={<NameRevealPage />} />
          {/* Overlay routes — keep matched under shell so deep-links don't 404 the background */}
          <Route path="/signin" element={null} />
          <Route path="/verify-identity" element={null} />
          <Route path="/settings" element={null} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      {isSignIn ? <SignInModal /> : null}
      {isVerifyIdentity ? <VerifyIdentityModal /> : null}
      {isSettings ? <SettingsModal /> : null}
    </>
  )
}

/**
 * Auth + discovery + Biddles + Stream Chat.
 */
export default function App() {
  return (
    <AuthProvider>
      <StreamChatProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </StreamChatProvider>
    </AuthProvider>
  )
}
